/**
 * API DIFFERENCES vs ../tooltip.tsx (shadcn):
 * - Content: TooltipPopup vs TooltipContent (also exported as TooltipContent for compat)
 * - Positioning: align/side/sideOffset on TooltipPopup vs sideOffset on TooltipContent
 * - Provider: Tooltip auto-wraps in Provider in shadcn vs separate TooltipProvider
 */
/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */
'use client'

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'
import { cn } from '../lib/utils'
import { ArrowSvg, ArrowSvgSmaller } from './arrow-svg'

const TooltipCreateHandle = TooltipPrimitive.createHandle
const TooltipProvider = TooltipPrimitive.Provider
const Tooltip = TooltipPrimitive.Root

function TooltipTrigger(props: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipPopup({
  className,
  align = 'center',
  sideOffset = 11,
  side = 'top',
  children,
  ...props
}: TooltipPrimitive.Popup.Props & {
  align?: TooltipPrimitive.Positioner.Props['align']
  side?: TooltipPrimitive.Positioner.Props['side']
  sideOffset?: TooltipPrimitive.Positioner.Props['sideOffset']
}) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        className="z-50 h-(--positioner-height) w-(--positioner-width) max-w-(--available-width) transition-[top,left,right,bottom,transform] data-instant:transition-none"
        data-slot="tooltip-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <TooltipPrimitive.Popup
          className={cn(
            'text-on-surface bg-surface-3 relative flex h-(--popup-height,auto) w-(--popup-width,auto) origin-(--transform-origin) rounded-md text-xs text-balance transition-[width,height,scale,opacity] duration-150 ease-out not-dark:bg-clip-padding data-ending-style:scale-98 data-ending-style:opacity-0 data-instant:duration-0 data-starting-style:scale-98 data-starting-style:opacity-0',
            className,
          )}
          data-slot="tooltip-popup"
          {...props}
        >
          <TooltipPrimitive.Viewport
            className="relative size-full overflow-clip px-(--viewport-inline-padding) py-1 [--viewport-inline-padding:--spacing(2)] **:data-current:w-[calc(var(--popup-width)-2*var(--viewport-inline-padding)-2px)] **:data-current:opacity-100 **:data-current:transition-opacity **:data-current:data-ending-style:opacity-0 data-instant:transition-none **:data-previous:w-[calc(var(--popup-width)-2*var(--viewport-inline-padding)-2px)] **:data-previous:truncate **:data-previous:opacity-100 **:data-previous:transition-opacity **:data-previous:data-ending-style:opacity-0 **:data-current:data-starting-style:opacity-0 **:data-previous:data-starting-style:opacity-0"
            data-slot="tooltip-viewport"
          >
            {children}
          </TooltipPrimitive.Viewport>
          <TooltipPrimitive.Arrow
            className={cn(
              'group',
              'data-[side=top]:-bottom-0 data-[side=top]:translate-y-[7.5px] data-[side=top]:rotate-180',
              'data-[side=bottom]:-top-0 data-[side=bottom]:translate-y-[-7.5px]',
              'data-[side=left]:right-0 data-[side=left]:translate-x-[8.7px] data-[side=left]:rotate-90',
              'data-[side=right]:left-0 data-[side=right]:translate-x-[-8.7px] data-[side=right]:-rotate-90',
            )}
            data-slot="tooltip-arrow"
          >
            <ArrowSvg className="group-data-[side=left]:hidden group-data-[side=right]:hidden" />
            <ArrowSvgSmaller className="group-data-[side=bottom]:hidden group-data-[side=top]:hidden" />
          </TooltipPrimitive.Arrow>
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export {
  TooltipCreateHandle,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipPopup,
  TooltipPopup as TooltipContent,
}
